import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/index.css";
import Layout from "@/components/Layout";
import Projects from "@/pages/Projects";
import Activity from "@/pages/Activity";
import People from "@/pages/People";
import SOPs from "@/pages/SOPs";
import AskAnything from "@/pages/AskAnything";
import Inbox from "@/pages/Inbox";
import Calendar from "@/pages/Calendar";
import Settings from "@/pages/Settings";
import Commitments from "@/pages/Commitments";
import NeedsAttention from "@/pages/NeedsAttention";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import DemoTour from "@/pages/DemoTour";
import AuthCallback from "@/components/AuthCallback";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AskProvider } from "@/context/AskContext";
import { PersonPanelProvider } from "@/context/PersonPanelContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { RemindersProvider } from "@/context/RemindersContext";
import { PersonPanel } from "@/components/PersonPanel";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="h-8 w-8 rounded-full border-2 border-neutral-900 border-t-transparent animate-spin" />
    </div>
  );
}

function RequireAuth({ children, requireOnboarding = false }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user === false) return <Navigate to="/login" replace />;
  if (requireOnboarding && !user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

// LandingOrDashboard — sits at path "/". Toggles the #landing-shell iframe
// (declared in public/index.html) based on auth state:
//   • unauthenticated + on "/"  → keep landing overlay visible, render nothing
//   • unauthenticated + not "/" → normal redirect to /login
//   • authenticated              → hide overlay, render <Layout />
// Sub-routes (/projects, /inbox, …) render via Layout's <Outlet />.
function LandingOrDashboard() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const shell = document.getElementById("landing-shell");
    if (!shell) return;
    const isRoot = location.pathname === "/";
    const showLanding = isRoot && user === false;
    if (showLanding) {
      if (!shell.src || shell.src.indexOf("/landing.html") === -1) {
        shell.src = "/landing.html";
      }
      shell.style.display = "block";
    } else {
      shell.style.display = "none";
    }
  }, [user, location.pathname]);

  if (user === null) return <LoadingScreen />;
  if (user === false) {
    // On "/", the overlay iframe handles the view. On any other path under
    // this route, kick the user to sign-in like RequireAuth would.
    if (location.pathname === "/") return null;
    return <Navigate to="/login" replace />;
  }
  if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return <Layout />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/tour" element={<DemoTour />} />
      <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
      <Route path="/" element={<LandingOrDashboard />}>
        {/* Home is Ask Anything — multi-thread agentic AI. */}
        <Route index element={<AskAnything />} />
        <Route path="projects" element={<Projects />} />
        <Route path="attention" element={<NeedsAttention />} />
        <Route path="commitments" element={<Commitments />} />
        <Route path="activity" element={<Activity />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="people" element={<People />} />
        <Route path="sops" element={<SOPs />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AskProvider>
              <PersonPanelProvider>
                <WorkspaceProvider>
                  <RemindersProvider>
                    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
                      <AppRouter />
                      <PersonPanel />
                      <Toaster theme="light" position="top-right" richColors offset={88} data-testid="global-toaster" />
                    </TooltipProvider>
                  </RemindersProvider>
                </WorkspaceProvider>
              </PersonPanelProvider>
            </AskProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
